// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getUserById, updateUserStatus } from '../services/userService';
import {
  getUserRoleAssignments,
  createRoleAssignment,
  deleteRoleAssignment,
} from '../services/roleAssignmentService';
import { useRoleCatalog } from './useRoleCatalog';
import { iamRefusalMessage } from './iamRefusal';
import type { RoleOption } from './useRoleCatalog';
import { useSharedSession } from '../auth/useSharedSession';
import type { SharedSite } from '../auth/useSharedSession';
import { toUserRole, deriveEffectiveRole } from '../../helios/services/authService';
import type { UserRole } from '../../helios/types';
import type { UserWithOrg } from '../types/user';
import type { RoleAssignmentDTO } from '../types/iam';

export type { RoleOption } from './useRoleCatalog';

export interface SiteAccessRow extends SharedSite {
  granted: boolean;
  grantedAt: string | null;
  /** True when `granted` can't be revoked by the per-site checkbox: org-wide
   *  access, or access inherited from a REGION-scoped assignment that this
   *  row has no direct SITE assignment id to delete. */
  locked: boolean;
}

export interface UserDetailView {
  user: UserWithOrg | null;
  isLoading: boolean;
  error: string | null;
  /** False once the user, their role assignments, or the role catalogue
   *  failed to load (or the BFF itself couldn't resolve access) — role/site
   *  mutations are unsafe without a true read of current assignments, so
   *  callers should disable those controls rather than mutate blind. */
  canEditAccess: boolean;

  /** The single rank-based role this screen's dropdown edits (ADR-0026 — a
   *  user may really hold several role assignments; this UI, like the
   *  mockup, only ever shows/edits one "Organization Role"). */
  effectiveRole: UserRole | null;
  /** Canonical roles this tenant's role catalogue actually has an id for —
   *  a role with no matching `RoleDTO` can't be assigned and is left out. */
  roleOptions: RoleOption[];
  /** True when the role catalogue loaded but has no roles to offer. */
  roleCatalogEmpty: boolean;
  /** Grants `role`. For a user with no role, `siteIds` grants it at those sites
   *  only (one assignment per site) instead of the whole organization. */
  setRole: (role: UserRole, siteIds?: string[]) => Promise<void>;
  isSettingRole: boolean;
  /** "No role": DELETEs every current assignment (org, region and site) so the
   *  user has no access. A 404 counts as removed. The OrgAdmin role goes
   *  first, and if that fails nothing else is removed. Rejects if any DELETE
   *  fails — some assignments may then remain, and the refetched page shows
   *  what is actually left. */
  removeAllRoles: () => Promise<void>;

  /** True when the user holds an ORGANIZATION-scoped assignment: every site
   *  in the org, not itemized per-site. The per-site checkboxes below are
   *  shown granted-and-locked in that case — see `toggleSite`'s docstring. */
  allSites: boolean;
  siteAccess: SiteAccessRow[];
  toggleSite: (siteId: string) => Promise<void>;
  isTogglingSite: boolean;

  status: string | null;
  setStatus: (status: 'active' | 'inactive') => Promise<void>;
  isSettingStatus: boolean;

  mutationError: string | null;
}

/**
 * True when `a` currently grants access: not soft-deactivated and not past
 * its `expiresAt`. Mirrors the BFF's own `active_assignments()`
 * (`back/app/composition/users/access.py`) so this screen and the list it
 * returns to agree on what "active" means — including that an offset-free
 * `expiresAt` (no trailing `Z`/UTC offset) is naive UTC, not local time, so a
 * plain `new Date(expiresAt)` would misjudge expiry by the viewer's own
 * timezone offset.
 */
export function isAssignmentCurrent(a: RoleAssignmentDTO, now: number = Date.now()): boolean {
  if (a.isActive === false) return false;
  if (!a.expiresAt) return true;
  const hasOffset = /Z$|[+-]\d{2}:?\d{2}$/.test(a.expiresAt);
  const expiresMs = Date.parse(hasOffset ? a.expiresAt : `${a.expiresAt}Z`);
  return Number.isNaN(expiresMs) || expiresMs > now;
}

/**
 * Where a role is granted by default to a user who has none: Member (Operator)
 * at specific sites when the role allows it, every other role at the whole
 * organization, or at sites when the role can't be held org-wide (AB#35975).
 * The admin can switch before anything is sent.
 */
export function defaultGrantScope(option: RoleOption): 'SITE' | 'ORGANIZATION' {
  if (!option.scopes.includes('ORGANIZATION')) return 'SITE';
  return option.role === 'operator' && option.scopes.includes('SITE') ? 'SITE' : 'ORGANIZATION';
}

function scopeOf(a: RoleAssignmentDTO): string {
  return (a.scopeType ?? '').trim().toUpperCase();
}

function findPrimaryAssignment(
  assignments: RoleAssignmentDTO[],
  effectiveRole: UserRole | null
): RoleAssignmentDTO | null {
  if (!effectiveRole) return null;
  // A user can hold several assignments at the same rank-selected role (e.g.
  // the same role at two regions); this screen edits one "Organization Role"
  // so it picks the first and leaves the rest untouched. Documented as a
  // known simplification vs. ADR-0026's full per-scope model (ADO #22697 PR).
  return (
    assignments.find(
      a => isAssignmentCurrent(a) && toUserRole(a.roleName ?? '') === effectiveRole
    ) ?? null
  );
}

const SAVE_FAILED = 'That change could not be saved. Please try again.';

/** A DELETE whose assignment is already gone. IAM codes its 404s, so
 *  `iamRefusalMessage` alone would read one as a refusal. */
function isNotFound(error: unknown): boolean {
  return (error as { status?: number } | null)?.status === 404;
}

/** A role swap whose revoke failed and whose rollback of the new grant failed too. */
class BothRolesKeptError extends Error {
  constructor(readonly refusal: string) {
    super('The new role was granted but neither role could be removed.');
    this.name = 'BothRolesKeptError';
  }
}

/** "No role" removed some assignments but not all of them. */
class PartialRemovalError extends Error {
  constructor(readonly cause: unknown) {
    super("Some roles couldn't be removed. This page shows what this user still has. Try again.");
    this.name = 'PartialRemovalError';
  }
}

/** Refused by this screen before any request was sent; its message is written for the admin. */
class NotReadyError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'NotReadyError';
  }
}

function describeMutationError(error: Error): string {
  if (error instanceof NotReadyError || error instanceof PartialRemovalError) return error.message;
  if (error instanceof BothRolesKeptError) {
    return `${error.refusal} The new role couldn't be removed again, so this user now has both roles. Refresh to check.`;
  }
  return iamRefusalMessage(error) ?? SAVE_FAILED;
}

/**
 * Composed view model for the user detail / edit screen (ADO #22697):
 * `GET /users/{id}` for profile + status, `GET /role-assignments/user/{id}`
 * for the editable assignment ids `UserResponse.role_assignments` doesn't
 * carry, `GET /roles` to resolve a chosen canonical role back to a `roleId`,
 * and the org's sites (via `useSharedSession`) for the Site Access table.
 */
export function useUserDetail(userId: string | undefined): UserDetailView {
  const queryClient = useQueryClient();
  const { organizationId, sites } = useSharedSession();

  const userQuery = useQuery({
    queryKey: ['admin-center', 'user-detail', userId],
    queryFn: () => getUserById(userId as string),
    enabled: !!userId,
  });

  const assignmentsQuery = useQuery({
    queryKey: ['admin-center', 'user-assignments', userId],
    queryFn: () => getUserRoleAssignments(userId as string),
    enabled: !!userId,
  });

  const { roleOptions, isLoading: rolesLoading, isError: rolesError } = useRoleCatalog();

  const user = userQuery.data ?? null;
  const assignments = assignmentsQuery.data ?? [];
  const effectiveRole = user ? deriveEffectiveRole(user) : null;

  // Unknown access (a failed read) must never be treated as "no access": a
  // role/site mutation computed against an empty `assignments` array could
  // create a duplicate grant instead of replacing the real one, or drop an
  // assignment it never saw. `user.access_resolved === false` covers the
  // BFF's own composed read (`GET /users/{id}`) failing the same lookup.
  const accessUnknown = assignmentsQuery.isError || user?.access_resolved === false;
  const canEditAccess = !accessUnknown;

  function invalidateDetail() {
    // Returned (not fire-and-forget): react-query keeps a mutation "pending"
    // until its onSuccess/onSettled promise resolves, so returning this is
    // what keeps controls disabled until the refetched data actually lands —
    // otherwise a fast second click can race the stale pre-write data still
    // sitting in `assignments`/`user` (e.g. deleting an already-deleted
    // assignment, or creating a duplicate grant).
    return Promise.all([
      queryClient.invalidateQueries({ queryKey: ['admin-center', 'user-detail', userId] }),
      queryClient.invalidateQueries({ queryKey: ['admin-center', 'user-assignments', userId] }),
      // The Users list table also shows role/status — keep it in sync too.
      queryClient.invalidateQueries({ queryKey: ['admin-center', 'users'] }),
    ]);
  }

  const roleMutation = useMutation({
    mutationFn: async ({ role, siteIds }: { role: UserRole; siteIds?: string[] }) => {
      if (!userId || !user) throw new Error('User not loaded yet.');
      if (accessUnknown) {
        throw new Error("This user's current access couldn't be read. Refresh and try again.");
      }
      const option = roleOptions.find(o => o.role === role);
      if (!option) throw new Error(`No role assignment configured for "${role}" in this tenant.`);

      const current = findPrimaryAssignment(assignments, effectiveRole);
      if (siteIds) {
        // An empty list is a caller mistake, not "the organization": it must not
        // fall through to an org-wide grant.
        if (siteIds.length === 0) throw new NotReadyError('Pick at least one site.');
        // A first grant at chosen sites. With a role already held, the site
        // checkboxes are the editor, so this is refused rather than stacking roles.
        if (current) throw new NotReadyError("Change this user's sites from Site access.");
        if (!option.scopes.includes('SITE')) {
          throw new NotReadyError(`${role} can't be granted at a single site.`);
        }
        for (const siteId of siteIds) {
          await createRoleAssignment({ userId, roleId: option.roleId, resourceId: siteId });
        }
        return;
      }
      // Preserve the existing assignment's scope (site/region) when swapping
      // roles, but only when the new role is actually assignable at that
      // scope (`RoleDTO.applicableScopes`) — e.g. swapping a SITE-scoped
      // Operator for OrgAdmin (ORGANIZATION-only) can't reuse the site as the
      // new assignment's resource. Re-scope to the org when the new role
      // supports it; otherwise there's no scope this screen can pick on the
      // admin's behalf, so this is refused rather than sending IAM an
      // invalid role/scope pair. A finer per-scope editor is out of this
      // ticket's scope (ADR-0026 note in the PR description).
      const currentScope = current ? scopeOf(current) || 'ORGANIZATION' : null;
      let resourceId: string | undefined;
      if (current && currentScope && option.scopes.includes(currentScope)) {
        resourceId = current.resourceId;
      } else if (option.scopes.includes('ORGANIZATION')) {
        resourceId = user.organization_id ?? organizationId ?? undefined;
      }
      if (!resourceId) {
        throw new Error(
          current
            ? `${role} can't be assigned at this user's current scope from this screen.`
            : `${role} needs a site or region — set it from Site Access instead.`
        );
      }

      // Create before delete: if creating the replacement fails, the user
      // keeps their existing role instead of being left with none.
      // No `expiresAt`: IAM doesn't support expiry, so an assignment lasts
      // until it is deleted.
      const created = await createRoleAssignment({ userId, roleId: option.roleId, resourceId });
      if (!current) return;
      try {
        await deleteRoleAssignment(current.scopeAssignmentId);
      } catch (error) {
        // IAM can refuse the revoke (e.g. the last admin's role, AB#35966):
        // undo the grant so the user isn't left holding both roles. Only for
        // a refusal: after a 404, 5xx or network error the old role may
        // already be gone, and undoing the grant would leave no role at all.
        const refusal = iamRefusalMessage(error);
        if (refusal === null || isNotFound(error)) throw error;
        try {
          await deleteRoleAssignment(created.scopeAssignmentId);
        } catch {
          throw new BothRolesKeptError(refusal);
        }
        throw error;
      }
    },
    onSettled: invalidateDetail,
  });

  const siteMutation = useMutation({
    mutationFn: async (siteId: string) => {
      if (!userId) throw new Error('User not loaded yet.');
      if (accessUnknown) {
        throw new Error("This user's current access couldn't be read. Refresh and try again.");
      }
      // A site can (rarely) carry more than one active direct assignment —
      // revoke all of them, not just the first, or the checkbox would
      // silently re-check itself once the refetch exposes the survivor.
      const existingAtSite = assignments.filter(
        a => isAssignmentCurrent(a) && scopeOf(a) === 'SITE' && a.resourceId === siteId
      );
      if (existingAtSite.length > 0) {
        const results = await Promise.allSettled(
          existingAtSite.map(a => deleteRoleAssignment(a.scopeAssignmentId))
        );
        const failed = results.find(r => r.status === 'rejected');
        if (failed && failed.status === 'rejected') throw failed.reason;
        return;
      }
      // Granting a new site keeps the user's current Organization Role at
      // that site, rather than asking "which role at this site" separately —
      // the mockup has one role picker, not one per site. Only valid when
      // that role can actually be held at SITE scope (e.g. not Executive).
      const option = roleOptions.find(o => o.role === effectiveRole);
      if (!option) throw new Error('Set an Organization Role before granting site access.');
      if (!option.scopes.includes('SITE')) {
        throw new Error(`${effectiveRole} can't be granted at a single site.`);
      }
      await createRoleAssignment({ userId, roleId: option.roleId, resourceId: siteId });
    },
    onSettled: invalidateDetail,
  });

  const removeAllMutation = useMutation({
    mutationFn: async () => {
      if (!userId || !user) throw new NotReadyError('User not loaded yet.');
      if (accessUnknown) {
        throw new NotReadyError(
          "This user's current access couldn't be read. Refresh and try again."
        );
      }
      if (assignmentsQuery.data === undefined) {
        throw new NotReadyError(
          "This user's role assignments are still loading. Try again in a moment."
        );
      }
      const current = assignments.filter(a => isAssignmentCurrent(a));
      // The role shown comes from the user record and the deletes from the
      // assignment list; if they disagree, deleting nothing must not read as success.
      if (current.length === 0 && effectiveRole) {
        throw new NotReadyError(
          'No current role assignment was found to remove. Refresh and try again.'
        );
      }
      const isAdmin = (a: RoleAssignmentDTO) => toUserRole(a.roleName ?? '') === 'admin';
      // A 404 means the assignment is already gone (another admin, or a stale
      // list), which is what this action wanted, so it counts as removed.
      const failedOf = (results: PromiseSettledResult<void>[]) =>
        results.find(
          (r): r is PromiseRejectedResult => r.status === 'rejected' && !isNotFound(r.reason)
        );
      // OrgAdmin first, and any failure there stops the rest: IAM refuses
      // removing your own or the last admin role (AB#35966), and after a 5xx or
      // network error the outcome is unknown, so removing the others would
      // leave a half-applied state.
      const adminResults = await Promise.allSettled(
        current.filter(isAdmin).map(a => deleteRoleAssignment(a.scopeAssignmentId))
      );
      const adminFailed = failedOf(adminResults);
      if (adminFailed) throw adminFailed.reason;
      // Every other DELETE is attempted even if one fails, so a partial failure
      // leaves as little access behind as possible; it is still reported.
      const results = [
        ...adminResults,
        ...(await Promise.allSettled(
          current.filter(a => !isAdmin(a)).map(a => deleteRoleAssignment(a.scopeAssignmentId))
        )),
      ];
      const failed = failedOf(results);
      if (!failed) return;
      // Once anything was removed, the generic "could not be saved" would read
      // as if nothing changed.
      const removedSome = results.some(r => r.status === 'fulfilled' || isNotFound(r.reason));
      throw removedSome ? new PartialRemovalError(failed.reason) : failed.reason;
    },
    onSettled: invalidateDetail,
  });

  const statusMutation = useMutation({
    mutationFn: async (status: 'active' | 'inactive') => {
      if (!userId) throw new Error('User not loaded yet.');
      await updateUserStatus(userId, status);
    },
    onSettled: invalidateDetail,
  });

  const allSites = user?.all_sites ?? false;
  // `user.sites` is the BFF's already-resolved effective set (SITE grants
  // plus every site under a REGION grant, see `app/composition/users/access.py`)
  // — the source of truth for "is this site actually granted", not just the
  // direct SITE assignments this screen also needs for their assignment ids.
  // A site granted only by inheritance (present in `user.sites` but with no
  // matching direct SITE assignment here) has nothing this screen can delete,
  // so it's shown granted-and-locked rather than falsely offered as revocable.
  const effectiveSiteIds = new Set((user?.sites ?? []).map(s => s.site_id));
  const siteAccess: SiteAccessRow[] = sites.map(site => {
    const assignment = assignments.find(
      a => isAssignmentCurrent(a) && scopeOf(a) === 'SITE' && a.resourceId === site.id
    );
    const granted = allSites || !!assignment || effectiveSiteIds.has(site.id);
    return {
      ...site,
      granted,
      grantedAt: assignment?.assignedAt ?? null,
      locked: allSites || (granted && !assignment),
    };
  });

  // The most recent failure: an older mutation's error stays set until that
  // mutation runs again, and its reason no longer applies to the latest action.
  const failure = [roleMutation, removeAllMutation, siteMutation, statusMutation]
    .filter(m => m.error)
    .sort((a, b) => b.submittedAt - a.submittedAt)[0]?.error;
  const mutationError = failure ? describeMutationError(failure) : null;

  // A catalogue that loaded but is empty: shown next to the role picker, not as a
  // page-level load error, because nothing failed to load.
  const roleCatalogEmpty = !rolesLoading && !rolesError && roleOptions.length === 0;

  const error = userQuery.isError
    ? 'Unable to load this user.'
    : accessUnknown
      ? "Unable to load this user's role and site access. Try again before making changes."
      : rolesError
        ? 'Unable to load the role catalog. Role changes are unavailable until this loads.'
        : null;

  return {
    user,
    isLoading: userQuery.isPending || assignmentsQuery.isPending,
    error,
    canEditAccess,

    effectiveRole,
    roleOptions,
    roleCatalogEmpty,
    setRole: (role, siteIds) => roleMutation.mutateAsync({ role, siteIds }),
    isSettingRole: roleMutation.isPending || removeAllMutation.isPending,
    removeAllRoles: () => removeAllMutation.mutateAsync(),

    allSites,
    siteAccess,
    toggleSite: siteId => siteMutation.mutateAsync(siteId),
    isTogglingSite: siteMutation.isPending,

    status: user?.status ?? null,
    setStatus: status => statusMutation.mutateAsync(status),
    isSettingStatus: statusMutation.isPending,

    mutationError,
  };
}
